import React, { useState, useEffect, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { loadStripe } from '@stripe/stripe-js';
import { Elements, PaymentElement, useStripe, useElements } from '@stripe/react-stripe-js';
import { Check, Sparkles, ArrowRight, ArrowLeft, Sun, Moon, Shield, Loader2, CreditCard, Lock, Star, Clock, ChevronLeft, ChevronRight, Quote } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { useTheme } from '../context/ThemeContext';
import { API_URL } from '../config/api';

const LOGOS = {
  dark: 'https://customer-assets.emergentagent.com/job_store-boost-1/artifacts/p00q8cnd_logo_darkversion.6219654a445e56ed3cd0.png',
  light: 'https://customer-assets.emergentagent.com/job_store-boost-1/artifacts/40mbqv8p_logo.a291411cbf7fc3847246.png'
};

// Stripe Price IDs
const STRIPE_PRICES = {
  visibility: {
    monthly: 'price_1SmDuDGesgZtCnAlz1o8RdBY',
    yearly: 'price_1SmDuDGesgZtCnAlkEYrVUfL'
  },
  premium: {
    monthly: 'price_1LuCsUGesgZtCnAl67DYjOoq',
    yearly: 'price_1LuCsUGesgZtCnAlXWkTFn5s'
  }
};

// Reviews data - 15 recensioni per lo slider
const REVIEWS = [
  { name: 'Marco R.', business: 'Barber Shop Milano', rating: 5, text: 'In 2 settimane ho ricevuto 15 nuovi clienti. Incredibile!' },
  { name: 'Giulia P.', business: 'Centro Estetico Roma', rating: 5, text: 'Finalmente una piattaforma che funziona. ROI pazzesco!' },
  { name: 'Andrea S.', business: 'Parrucchiere Napoli', rating: 5, text: 'Il programma Elit mi ha portato clienti senza spendere in ads.' },
  { name: 'Francesca M.', business: 'Nail Art Torino', rating: 5, text: 'Semplicissimo da usare, risultati immediati.' },
  { name: 'Luca B.', business: 'Barbiere Firenze', rating: 5, text: 'La garanzia 6 mesi mi ha convinto. Ora sono super soddisfatto!' },
  { name: 'Sara C.', business: 'Salone Bellezza Bologna', rating: 5, text: 'Ho raddoppiato le prenotazioni in un mese. Consigliatissimo!' },
  { name: 'Giovanni M.', business: 'Barber Lab Verona', rating: 5, text: 'Investimento migliore per il mio salone. Clienti nuovi ogni settimana.' },
  { name: 'Elena T.', business: 'Beauty Center Padova', rating: 5, text: 'Dashboard intuitiva e risultati tangibili. Top!' },
  { name: 'Roberto F.', business: 'Hair Style Genova', rating: 5, text: 'Finalmente visibilità online senza stress. Grazie Skipres!' },
  { name: 'Alessia B.', business: 'Nails & Beauty Bari', rating: 5, text: 'Clienti che prima non mi trovavano ora prenotano regolarmente.' },
  { name: 'Davide L.', business: 'The Barber Catania', rating: 5, text: 'Assistenza top e risultati garantiti. 5 stelle meritate!' },
  { name: 'Martina G.', business: 'Estetica Moderna Trieste', rating: 5, text: 'Il programma Elit è una bomba! Zero rischi, solo guadagni.' },
  { name: 'Fabio S.', business: 'Cut & Style Perugia', rating: 5, text: 'Agenda sempre piena grazie a Skipres. Non torno indietro!' },
  { name: 'Chiara N.', business: 'Beauty Lab Ancona', rating: 5, text: 'Professionalità e risultati concreti. Sono entusiasta!' },
  { name: 'Simone P.', business: 'Urban Barber Brescia', rating: 5, text: 'In 3 mesi ho recuperato l\'investimento 10 volte. Pazzesco!' }
];

const stripePromise = loadStripe('pk_live_L7QSVBceLce5MOkph4OcHHvi00xJcatjq6');

// Compact Countdown Badge Component
const CountdownBadge = ({ isDark }) => {
  const [timeLeft, setTimeLeft] = useState(() => {
    const saved = sessionStorage.getItem('checkoutCountdown');
    if (saved) {
      const remaining = parseInt(saved) - Date.now();
      return remaining > 0 ? Math.floor(remaining / 1000) : 3600;
    }
    sessionStorage.setItem('checkoutCountdown', String(Date.now() + 3600000));
    return 3600;
  });

  useEffect(() => {
    const timer = setInterval(() => {
      setTimeLeft(prev => {
        if (prev <= 1) {
          sessionStorage.setItem('checkoutCountdown', String(Date.now() + 3600000));
          return 3600;
        }
        return prev - 1;
      });
    }, 1000);
    return () => clearInterval(timer);
  }, []);

  const minutes = Math.floor(timeLeft / 60);
  const seconds = timeLeft % 60;

  return (
    <div className={`flex items-center justify-center gap-2 py-2 px-3 rounded-t-xl border border-b-0 ${isDark ? 'bg-red-500/20 border-red-500/30' : 'bg-red-50 border-red-200'}`}>
      <Clock className="w-3.5 h-3.5 text-red-500 animate-pulse" />
      <span className={`text-xs ${isDark ? 'text-red-300' : 'text-red-600'}`}>Offerta scade tra</span>
      <span className="font-mono text-sm font-bold text-red-500">
        {String(minutes).padStart(2, '0')}:{String(seconds).padStart(2, '0')}
      </span>
    </div>
  );
};

// Payment Icons - Icone metodi di pagamento
const PaymentIcons = ({ isDark }) => (
  <div className="flex items-center justify-center flex-wrap gap-2">
    {/* Visa */}
    <img src="https://js.stripe.com/v3/fingerprinted/img/visa-729c05c240c4bdb47b03ac81d9945bfe.svg" alt="Visa" className="h-6" />
    {/* Mastercard */}
    <img src="https://js.stripe.com/v3/fingerprinted/img/mastercard-4d8844094130711885b5e41b28c9848f.svg" alt="Mastercard" className="h-6" />
    {/* Amex */}
    <img src="https://js.stripe.com/v3/fingerprinted/img/amex-a49b82f46c5cd6a96a6e418a6ca1717c.svg" alt="Amex" className="h-6" />
    {/* SEPA */}
    <div className={`px-2 py-1 rounded text-[10px] font-semibold ${isDark ? 'bg-blue-500/20 text-blue-400' : 'bg-blue-100 text-blue-700'}`}>SEPA</div>
    {/* Klarna - Logo ufficiale rosa */}
    <div className="bg-[#FFB3C7] px-2 py-1 rounded">
      <span className="text-[#0A0B09] text-[10px] font-bold">Klarna.</span>
    </div>
    {/* Apple Pay - Logo ufficiale */}
    <div className="bg-black px-2 py-1 rounded flex items-center gap-0.5">
      <svg className="h-4 w-4" viewBox="0 0 24 24" fill="white">
        <path d="M17.05 20.28c-.98.95-2.05.88-3.08.4-1.09-.5-2.08-.48-3.24 0-1.44.62-2.2.44-3.06-.4C2.79 15.25 3.51 7.59 9.05 7.31c1.35.07 2.29.74 3.08.8 1.18-.24 2.31-.93 3.57-.84 1.51.12 2.65.72 3.4 1.8-3.12 1.87-2.38 5.98.48 7.13-.57 1.5-1.31 2.99-2.53 4.08zM12.03 7.25c-.15-2.23 1.66-4.07 3.74-4.25.29 2.58-2.34 4.5-3.74 4.25z"/>
      </svg>
      <span className="text-white text-[10px] font-medium">Pay</span>
    </div>
    {/* Google Pay - Logo ufficiale */}
    <div className={`px-2 py-1 rounded border flex items-center gap-0.5 ${isDark ? 'bg-slate-800 border-slate-600' : 'bg-white border-slate-300'}`}>
      <svg className="h-4 w-4" viewBox="0 0 24 24">
        <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
        <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
        <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
        <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
      </svg>
      <span className={`text-[10px] font-medium ${isDark ? 'text-white' : 'text-slate-700'}`}>Pay</span>
    </div>
  </div>
);

// Reviews Slider Component (for Step 2 - compact)
const ReviewsSlider = ({ isDark }) => {
  const [currentIndex, setCurrentIndex] = useState(0);

  const nextSlide = useCallback(() => {
    setCurrentIndex(prev => (prev + 1) % REVIEWS.length);
  }, []);

  const prevSlide = () => {
    setCurrentIndex(prev => (prev - 1 + REVIEWS.length) % REVIEWS.length);
  };

  useEffect(() => {
    const timer = setInterval(nextSlide, 5000);
    return () => clearInterval(timer);
  }, [nextSlide]);

  const review = REVIEWS[currentIndex];

  return (
    <div className={`rounded-xl p-4 border ${isDark ? 'bg-slate-800/50 border-slate-700' : 'bg-white border-slate-200 shadow-sm'}`}>
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-1">
          {[...Array(5)].map((_, i) => (
            <Star key={i} className={`w-4 h-4 ${i < review.rating ? 'text-amber-400 fill-amber-400' : 'text-slate-300'}`} />
          ))}
        </div>
        <div className="flex gap-1">
          <button onClick={prevSlide} className={`p-1 rounded ${isDark ? 'hover:bg-slate-700' : 'hover:bg-slate-100'}`}>
            <ChevronLeft className={`w-4 h-4 ${isDark ? 'text-slate-400' : 'text-slate-500'}`} />
          </button>
          <button onClick={nextSlide} className={`p-1 rounded ${isDark ? 'hover:bg-slate-700' : 'hover:bg-slate-100'}`}>
            <ChevronRight className={`w-4 h-4 ${isDark ? 'text-slate-400' : 'text-slate-500'}`} />
          </button>
        </div>
      </div>
      <div className="relative">
        <Quote className={`absolute -top-1 -left-1 w-6 h-6 ${isDark ? 'text-emerald-500/20' : 'text-emerald-500/30'}`} />
        <p className={`text-sm italic pl-4 ${isDark ? 'text-slate-300' : 'text-slate-600'}`}>
          &quot;{review.text}&quot;
        </p>
      </div>
      <div className="mt-3 flex items-center gap-2">
        <div className="w-8 h-8 rounded-full bg-gradient-to-br from-emerald-400 to-emerald-600 flex items-center justify-center text-white font-bold text-xs">
          {review.name.charAt(0)}
        </div>
        <div>
          <p className={`text-sm font-semibold ${isDark ? 'text-white' : 'text-slate-800'}`}>{review.name}</p>
          <p className={`text-xs ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>{review.business}</p>
        </div>
      </div>
      <div className="flex justify-center gap-1 mt-3">
        {REVIEWS.map((_, i) => (
          <div key={i} className={`w-1.5 h-1.5 rounded-full transition-all ${i === currentIndex ? 'bg-emerald-500 w-4' : isDark ? 'bg-slate-600' : 'bg-slate-300'}`} />
        ))}
      </div>
    </div>
  );
};

// Reviews Slider Component for Step 1 - 3 recensioni su desktop, 1 su mobile
const ReviewsSliderFull = ({ isDark }) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isAutoPlaying, setIsAutoPlaying] = useState(true);
  const itemsPerPage = 3; // Mostra 3 recensioni alla volta su desktop
  const totalPages = Math.ceil(REVIEWS.length / itemsPerPage);

  const nextSlide = useCallback(() => {
    setCurrentIndex(prev => (prev + 1) % totalPages);
  }, [totalPages]);

  const prevSlide = () => {
    setCurrentIndex(prev => (prev - 1 + totalPages) % totalPages);
  };

  const goToSlide = (index) => {
    setCurrentIndex(index);
    setIsAutoPlaying(false);
    setTimeout(() => setIsAutoPlaying(true), 5000);
  };

  useEffect(() => {
    if (!isAutoPlaying) return;
    const timer = setInterval(nextSlide, 5000);
    return () => clearInterval(timer);
  }, [nextSlide, isAutoPlaying]);

  // Ottieni le 3 recensioni correnti
  const startIndex = currentIndex * itemsPerPage;
  const visibleReviews = REVIEWS.slice(startIndex, startIndex + itemsPerPage);

  return (
    <div className="mt-8">
      <h3 className={`text-lg font-bold mb-4 text-center ${isDark ? 'text-white' : 'text-slate-900'}`}>
        ⭐ Cosa dicono i nostri clienti
      </h3>
      <div className="relative">
        {/* Navigation Arrows */}
        <button 
          onClick={prevSlide} 
          className={`absolute -left-4 top-1/2 -translate-y-1/2 p-2 rounded-full z-10 transition-all ${isDark ? 'bg-slate-800 hover:bg-slate-700 text-slate-400 hover:text-white' : 'bg-white shadow-lg hover:bg-slate-50 text-slate-500 hover:text-slate-800'}`}
        >
          <ChevronLeft className="w-5 h-5" />
        </button>
        <button 
          onClick={nextSlide} 
          className={`absolute -right-4 top-1/2 -translate-y-1/2 p-2 rounded-full z-10 transition-all ${isDark ? 'bg-slate-800 hover:bg-slate-700 text-slate-400 hover:text-white' : 'bg-white shadow-lg hover:bg-slate-50 text-slate-500 hover:text-slate-800'}`}
        >
          <ChevronRight className="w-5 h-5" />
        </button>

        {/* Reviews Grid - 3 colonne su desktop */}
        <div className="grid md:grid-cols-3 gap-4 px-2">
          {visibleReviews.map((review, index) => (
            <div key={startIndex + index} className={`rounded-xl p-4 border transition-all ${isDark ? 'bg-slate-800/50 border-slate-700' : 'bg-white border-slate-200 shadow-sm'}`}>
              {/* Stars */}
              <div className="flex items-center gap-1 mb-3">
                {[...Array(5)].map((_, i) => (
                  <Star key={i} className={`w-4 h-4 ${i < review.rating ? 'text-amber-400 fill-amber-400' : 'text-slate-300'}`} />
                ))}
              </div>
              
              {/* Quote */}
              <div className="relative mb-3">
                <Quote className={`absolute -top-1 -left-1 w-5 h-5 ${isDark ? 'text-emerald-500/20' : 'text-emerald-500/30'}`} />
                <p className={`text-sm italic pl-4 min-h-[3rem] ${isDark ? 'text-slate-300' : 'text-slate-600'}`}>
                  &quot;{review.text}&quot;
                </p>
              </div>
              
              {/* Author */}
              <div className="flex items-center gap-2 mt-auto">
                <div className="w-8 h-8 rounded-full bg-gradient-to-br from-emerald-400 to-emerald-600 flex items-center justify-center text-white font-bold text-xs">
                  {review.name.charAt(0)}
                </div>
                <div>
                  <p className={`text-sm font-semibold ${isDark ? 'text-white' : 'text-slate-800'}`}>{review.name}</p>
                  <p className={`text-xs ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>{review.business}</p>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Dots Indicator */}
        <div className="flex justify-center gap-2 mt-5">
          {[...Array(totalPages)].map((_, i) => (
            <button 
              key={i} 
              onClick={() => goToSlide(i)}
              className={`w-2 h-2 rounded-full transition-all ${i === currentIndex ? 'bg-emerald-500 w-6' : isDark ? 'bg-slate-600 hover:bg-slate-500' : 'bg-slate-300 hover:bg-slate-400'}`} 
            />
          ))}
        </div>
        
        {/* Counter */}
        <div className={`text-center mt-2 text-xs ${isDark ? 'text-slate-500' : 'text-slate-400'}`}>
          {currentIndex + 1} / {totalPages}
        </div>
      </div>
    </div>
  );
};

// Payment Form Component
const PaymentForm = ({ onSuccess, onError, isProcessing, setIsProcessing, isDark }) => {
  const stripe = useStripe();
  const elements = useElements();

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!stripe || !elements) return;
    setIsProcessing(true);
    try {
      const { error, paymentIntent } = await stripe.confirmPayment({
        elements,
        confirmParams: { return_url: `${window.location.origin}/success` },
        redirect: 'if_required'
      });
      if (error) {
        onError(error.message);
        setIsProcessing(false);
      } else if (paymentIntent && paymentIntent.status === 'succeeded') {
        onSuccess(paymentIntent);
      } else {
        onSuccess(paymentIntent);
      }
    } catch (err) {
      onError(err.message || 'Errore durante il pagamento');
      setIsProcessing(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div className={`p-4 rounded-xl border ${isDark ? 'bg-slate-900 border-slate-700' : 'bg-white border-slate-200'}`}>
        <PaymentElement options={{ layout: 'tabs', defaultValues: { billingDetails: { address: { country: 'IT' } } } }} />
      </div>
      <Button type="submit" disabled={!stripe || isProcessing} className="w-full bg-gradient-to-r from-emerald-500 to-emerald-600 hover:from-emerald-400 hover:to-emerald-500 text-white py-4 rounded-xl font-bold text-lg disabled:opacity-50">
        {isProcessing ? (<><Loader2 className="w-5 h-5 mr-2 animate-spin" />Elaborazione...</>) : (<><Lock className="w-5 h-5 mr-2" />Paga Ora</>)}
      </Button>
    </form>
  );
};

const Checkout = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { isDark, toggleTheme } = useTheme();
  const initialPlan = location.state || { type: 'monthly', basePlan: 'visibility', price: 4.99 };
  
  const [selectedPlanType, setSelectedPlanType] = useState(initialPlan.type);
  const [upgradeToPremium, setUpgradeToPremium] = useState(initialPlan.upgradeToPremium || false);
  const [addElit, setAddElit] = useState(initialPlan.addElit || false);
  const [showElitDetails, setShowElitDetails] = useState(false);
  const [showPremiumDetails, setShowPremiumDetails] = useState(false);
  
  const [step, setStep] = useState(1);
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [clientSecret, setClientSecret] = useState(null);
  const [subscriptionId, setSubscriptionId] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [error, setError] = useState(null);
  
  // Pre-load clientSecret in background when user starts typing
  const [preloadedSecret, setPreloadedSecret] = useState(null);
  const [isPreloading, setIsPreloading] = useState(false);

  // Ref for payment section to scroll to
  const paymentSectionRef = React.useRef(null);

  const t = {
    bg: isDark ? 'bg-slate-950' : 'bg-slate-50',
    bgCard: isDark ? 'bg-slate-900/50 border-slate-700' : 'bg-white border-slate-200 shadow-sm',
    text: isDark ? 'text-white' : 'text-slate-900',
    textSecondary: isDark ? 'text-slate-300' : 'text-slate-600',
    textMuted: isDark ? 'text-slate-400' : 'text-slate-500',
    border: isDark ? 'border-slate-700' : 'border-slate-200',
    input: isDark ? 'bg-slate-900 border-slate-700 text-white placeholder-slate-500' : 'bg-white border-slate-300 text-slate-900 placeholder-slate-400',
  };

  useEffect(() => { window.scrollTo({ top: 0, behavior: 'instant' }); }, []);

  const prices = { visibility: { monthly: 4.99, yearly: 49.90 }, premium: { monthly: 19.90, yearly: 214.90 } };
  const calculateTotal = () => prices[upgradeToPremium ? 'premium' : 'visibility'][selectedPlanType];

  // Preload Stripe subscription when email is valid (runs in background)
  const preloadStripeSubscription = useCallback(async () => {
    if (!email || !email.includes('@') || !name.trim() || isPreloading || preloadedSecret) return;
    
    setIsPreloading(true);
    try {
      const planType = upgradeToPremium ? 'premium' : 'visibility';
      const priceId = STRIPE_PRICES[planType][selectedPlanType];
      const response = await fetch(`${API_URL}/api/checkout/create-subscription`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          price_id: priceId,
          plan_type: selectedPlanType,
          email,
          name,
          metadata: { plan: planType, elit_program: addElit ? 'true' : 'false', total: calculateTotal().toString() }
        })
      });
      const data = await response.json();
      if (response.ok && data.client_secret) {
        setPreloadedSecret({
          clientSecret: data.client_secret,
          subscriptionId: data.subscription_id,
          customerId: data.customer_id
        });
      }
    } catch (err) {
      console.log('Preload failed, will retry on submit:', err);
    }
    setIsPreloading(false);
  }, [email, name, upgradeToPremium, selectedPlanType, addElit, isPreloading, preloadedSecret, calculateTotal]);

  // Trigger preload when email looks valid and name is filled
  useEffect(() => {
    const timer = setTimeout(() => {
      if (email.includes('@') && name.trim().length >= 2 && !preloadedSecret) {
        preloadStripeSubscription();
      }
    }, 500); // Small delay to avoid too many requests
    return () => clearTimeout(timer);
  }, [email, name, preloadStripeSubscription, preloadedSecret]);

  // Reset preloaded secret when plan changes
  useEffect(() => {
    setPreloadedSecret(null);
  }, [selectedPlanType, upgradeToPremium, addElit]);

  const handleContinueToPayment = async () => {
    if (!email || !email.includes('@')) { setError('Inserisci un indirizzo email valido'); return; }
    if (!name.trim()) { setError('Inserisci il tuo nome'); return; }
    setError(null);
    
    // Use preloaded secret if available
    if (preloadedSecret) {
      setClientSecret(preloadedSecret.clientSecret);
      setSubscriptionId(preloadedSecret.subscriptionId);
      setStep(2);
      // Scroll to payment section on mobile
      setTimeout(() => {
        if (paymentSectionRef.current) {
          paymentSectionRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
        } else {
          window.scrollTo({ top: 0, behavior: 'smooth' });
        }
      }, 100);
      return;
    }
    
    // Fallback: create subscription now
    setIsLoading(true);
    try {
      const planType = upgradeToPremium ? 'premium' : 'visibility';
      const priceId = STRIPE_PRICES[planType][selectedPlanType];
      const response = await fetch(`${API_URL}/api/checkout/create-subscription`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          price_id: priceId,
          plan_type: selectedPlanType,
          email,
          name,
          metadata: { plan: planType, elit_program: addElit ? 'true' : 'false', total: calculateTotal().toString() }
        })
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || 'Errore durante la creazione del pagamento');
      setClientSecret(data.client_secret);
      setSubscriptionId(data.subscription_id);
      setStep(2);
      // Scroll to payment section
      setTimeout(() => {
        if (paymentSectionRef.current) {
          paymentSectionRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
        } else {
          window.scrollTo({ top: 0, behavior: 'smooth' });
        }
      }, 100);
    } catch (err) {
      console.error('Error:', err);
      setError(err.message || 'Si è verificato un errore. Riprova.');
    }
    setIsLoading(false);
  };

  const handlePaymentSuccess = () => {
    // Pass customer_id to success page for configuration linking
    const customerId = preloadedSecret?.customerId || null;
    navigate('/success', { state: { subscriptionId, customerId, plan: upgradeToPremium ? 'premium' : 'visibility', planType: selectedPlanType, elit: addElit, total: calculateTotal(), email } });
  };

  const handlePaymentError = (errorMessage) => { setError(errorMessage); };

  const stripeOptions = {
    clientSecret,
    appearance: {
      theme: isDark ? 'night' : 'stripe',
      variables: { colorPrimary: '#10b981', colorBackground: isDark ? '#0f172a' : '#ffffff', colorText: isDark ? '#ffffff' : '#1e293b', colorDanger: '#ef4444', fontFamily: 'system-ui, sans-serif', borderRadius: '12px', spacingUnit: '4px' },
      rules: { '.Input': { border: isDark ? '1px solid #334155' : '1px solid #e2e8f0', boxShadow: 'none', padding: '12px 16px' }, '.Input:focus': { border: '2px solid #10b981', boxShadow: '0 0 0 1px #10b981' }, '.Label': { fontWeight: '500', marginBottom: '8px' } }
    }
  };

  return (
    <div className={`min-h-screen py-8 md:py-12 transition-colors ${isDark ? 'bg-gradient-to-b from-slate-950 via-slate-900 to-slate-950' : 'bg-gradient-to-b from-slate-50 via-white to-slate-50'}`}>
      <div className="container mx-auto px-4">
        <div className="max-w-5xl mx-auto">
          {/* Header */}
          <div className="text-center mb-6">
            <img src={isDark ? LOGOS.dark : LOGOS.light} alt="Skipres" className="h-8 md:h-10 mx-auto mb-4" />
            <p className={t.textMuted}>{step === 1 ? 'Scegli il tuo piano' : 'Completa il pagamento'}</p>
          </div>

          {/* Progress Steps */}
          <div className="flex items-center justify-center gap-4 mb-8">
            <div className={`flex items-center gap-2 ${step >= 1 ? 'text-emerald-500' : t.textMuted}`}>
              <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold ${step >= 1 ? 'bg-emerald-500 text-white' : isDark ? 'bg-slate-800' : 'bg-slate-200'}`}>1</div>
              <span className="text-sm font-medium hidden sm:inline">Info</span>
            </div>
            <div className={`w-12 h-0.5 ${step >= 2 ? 'bg-emerald-500' : isDark ? 'bg-slate-700' : 'bg-slate-300'}`}></div>
            <div className={`flex items-center gap-2 ${step >= 2 ? 'text-emerald-500' : t.textMuted}`}>
              <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold ${step >= 2 ? 'bg-emerald-500 text-white' : isDark ? 'bg-slate-800' : 'bg-slate-200'}`}>2</div>
              <span className="text-sm font-medium hidden sm:inline">Pagamento</span>
            </div>
          </div>

          <div className="grid lg:grid-cols-5 gap-6">
            {/* Left: Plan Selection or Payment */}
            <div className="lg:col-span-3 space-y-4">
              {step === 1 ? (
                <>
                  {/* Current Plan */}
                  <div className={`backdrop-blur-sm border-2 border-emerald-500/50 rounded-xl p-4 ${isDark ? 'bg-slate-900/50' : 'bg-white'}`}>
                    <div className="flex items-start justify-between mb-3">
                      <div>
                        <Badge className="mb-1 bg-emerald-500 text-white text-xs">Piano Selezionato</Badge>
                        <h3 className={`text-lg md:text-xl font-bold ${t.text}`}>Piano Solo Visibilità</h3>
                        <p className={`text-xs md:text-sm ${t.textMuted}`}>La tua vetrina su 50.000+ utenti</p>
                      </div>
                      <div className="text-right">
                        <div className="text-2xl font-bold text-emerald-500">€{selectedPlanType === 'yearly' ? '4,16' : '4,99'}</div>
                        <div className={`text-xs ${t.textMuted}`}>/mese</div>
                      </div>
                    </div>
                    <div className="flex justify-center">
                      <div className={`rounded-full p-1 inline-flex border ${isDark ? 'bg-slate-800 border-slate-700' : 'bg-slate-100 border-slate-200'}`}>
                        <button onClick={() => setSelectedPlanType('monthly')} className={`px-4 py-1.5 rounded-full text-xs transition-all ${selectedPlanType === 'monthly' ? 'bg-emerald-500 text-white' : t.textMuted}`}>Mensile</button>
                        <button onClick={() => setSelectedPlanType('yearly')} className={`px-4 py-1.5 rounded-full text-xs transition-all relative ${selectedPlanType === 'yearly' ? 'bg-emerald-500 text-white' : t.textMuted}`}>
                          Annuale<Badge className="absolute -top-5 left-1/2 -translate-x-1/2 bg-red-500 text-white text-[10px] px-1.5">-50%</Badge>
                        </button>
                      </div>
                    </div>
                  </div>

                  {/* Upgrade Premium */}
                  <div className={`backdrop-blur-sm border-2 rounded-xl p-3 md:p-4 transition-all cursor-pointer ${upgradeToPremium ? 'border-purple-500/50 bg-purple-500/5' : `${t.bgCard}`}`} onClick={() => setUpgradeToPremium(!upgradeToPremium)}>
                    <div className="flex items-start gap-3">
                      <div className={`w-5 h-5 rounded border-2 flex items-center justify-center flex-shrink-0 mt-0.5 ${upgradeToPremium ? 'bg-purple-500 border-purple-500' : isDark ? 'border-slate-600' : 'border-slate-300'}`}>
                        {upgradeToPremium && <Check className="w-3 h-3 text-white" />}
                      </div>
                      <div className="flex-1">
                        <div className="flex flex-wrap items-center gap-1 mb-1"><Badge className="bg-purple-500 text-white text-[10px]">Upgrade</Badge></div>
                        <h3 className={`text-base md:text-lg font-bold ${t.text}`}>Piano Gestionale Completo</h3>
                        <div className="flex items-baseline gap-2 mb-2">
                          <span className="text-xl font-bold text-purple-500">€{selectedPlanType === 'yearly' ? '17,91' : '19,90'}</span>
                          <span className={`text-xs ${t.textMuted}`}>/mese</span>
                        </div>
                        <button onClick={(e) => { e.stopPropagation(); setShowPremiumDetails(!showPremiumDetails); }} className="text-purple-500 text-xs underline">{showPremiumDetails ? 'Nascondi' : 'Vedi cosa include'}</button>
                        {showPremiumDetails && (
                          <div className={`rounded-lg p-3 mt-2 space-y-1.5 ${isDark ? 'bg-slate-800/50' : 'bg-slate-50'}`}>
                            {['Agenda AI intelligente', 'Gestione operatori illimitati', 'CRM clienti avanzato', 'Statistiche e analytics'].map((f, i) => (
                              <div key={i} className={`flex items-start gap-2 ${t.textSecondary}`}><Check className="w-3 h-3 text-purple-500 mt-0.5" /><span className="text-xs">{f}</span></div>
                            ))}
                          </div>
                        )}
                      </div>
                      {/* Dashboard Mockup - affiancato a destra */}
                      <div className="hidden sm:flex gap-1.5 flex-shrink-0">
                        {/* Desktop mockup mini */}
                        <div className={`w-24 rounded-lg p-1.5 ${isDark ? 'bg-slate-800' : 'bg-slate-100'}`}>
                          <div className={`rounded ${isDark ? 'bg-slate-900' : 'bg-white'} p-1.5`}>
                            <div className="flex gap-0.5 mb-1.5">
                              <div className="w-1 h-1 rounded-full bg-red-400"></div>
                              <div className="w-1 h-1 rounded-full bg-yellow-400"></div>
                              <div className="w-1 h-1 rounded-full bg-green-400"></div>
                            </div>
                            <div className="grid grid-cols-3 gap-0.5 mb-1.5">
                              <div className={`rounded p-0.5 ${isDark ? 'bg-purple-500/20' : 'bg-purple-100'}`}>
                                <div className="text-purple-500 text-[7px] font-bold">€2.4K</div>
                              </div>
                              <div className={`rounded p-0.5 ${isDark ? 'bg-emerald-500/20' : 'bg-emerald-100'}`}>
                                <div className="text-emerald-500 text-[7px] font-bold">+32%</div>
                              </div>
                              <div className={`rounded p-0.5 ${isDark ? 'bg-blue-500/20' : 'bg-blue-100'}`}>
                                <div className="text-blue-500 text-[7px] font-bold">156</div>
                              </div>
                            </div>
                            <div className={`h-6 rounded ${isDark ? 'bg-slate-800' : 'bg-slate-50'} flex items-end px-0.5 gap-px`}>
                              {[40, 65, 45, 80, 60, 90, 75].map((h, i) => (
                                <div key={i} className="flex-1 bg-purple-500 rounded-t" style={{height: `${h}%`}}></div>
                              ))}
                            </div>
                          </div>
                        </div>
                        {/* Phone mockup mini */}
                        <div className={`w-10 rounded-xl ${isDark ? 'bg-slate-800' : 'bg-slate-200'} p-0.5`}>
                          <div className={`rounded-lg ${isDark ? 'bg-slate-900' : 'bg-white'} h-full p-1`}>
                            <div className={`w-3 h-0.5 mx-auto rounded ${isDark ? 'bg-slate-700' : 'bg-slate-300'} mb-1`}></div>
                            <div className={`rounded p-0.5 mb-1 ${isDark ? 'bg-purple-500/20' : 'bg-purple-100'}`}>
                              <div className="text-purple-500 text-[6px] font-bold text-center">€2.4K</div>
                            </div>
                            <div className={`h-5 rounded ${isDark ? 'bg-slate-800' : 'bg-slate-50'} flex items-end px-px gap-px`}>
                              {[50, 70, 40, 90, 60].map((h, i) => (
                                <div key={i} className="flex-1 bg-purple-500 rounded-t" style={{height: `${h}%`}}></div>
                              ))}
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Add Elit */}
                  <div className={`backdrop-blur-sm border-2 rounded-xl p-3 md:p-4 transition-all cursor-pointer ${addElit ? 'border-emerald-500/50 bg-emerald-500/5' : `${t.bgCard}`}`} onClick={() => setAddElit(!addElit)}>
                    <div className="flex items-start gap-3">
                      <div className={`w-5 h-5 rounded border-2 flex items-center justify-center flex-shrink-0 mt-0.5 ${addElit ? 'bg-emerald-500 border-emerald-500' : isDark ? 'border-slate-600' : 'border-slate-300'}`}>
                        {addElit && <Check className="w-3 h-3 text-white" />}
                      </div>
                      <div className="flex-1">
                        <div className="flex flex-wrap items-center gap-1 mb-1">
                          <Badge className="bg-gradient-to-r from-emerald-500 to-cyan-500 text-white text-[10px]"><Sparkles className="w-2.5 h-2.5 mr-0.5" />Elit</Badge>
                          <Badge className="bg-green-500 text-white text-[10px]">GRATUITO</Badge>
                        </div>
                        <h3 className={`text-base md:text-lg font-bold ${t.text}`}>Programma Elit</h3>
                        <p className={`text-xs mb-2 ${t.textSecondary}`}><span className="text-emerald-500 font-semibold">Noi investiamo in ads per te</span> - paghi solo 20% sui risultati</p>
                        <button onClick={(e) => { e.stopPropagation(); setShowElitDetails(!showElitDetails); }} className="text-emerald-500 text-xs underline">{showElitDetails ? 'Nascondi' : 'Come funziona'}</button>
                        {showElitDetails && (
                          <div className={`rounded-lg p-3 mt-2 ${isDark ? 'bg-emerald-500/10 border border-emerald-500/30' : 'bg-emerald-50 border border-emerald-200'}`}>
                            <div className={`space-y-1.5 text-xs ${t.textSecondary}`}>
                              <p>1. <span className="text-emerald-500 font-semibold">Noi spendiamo</span> in pubblicità Google/Facebook</p>
                              <p>2. <span className="text-emerald-500 font-semibold">Tu ricevi</span> nuovi clienti</p>
                              <p>3. <span className="text-emerald-500 font-semibold">Paghi 20%</span> solo sulle vendite generate</p>
                            </div>
                          </div>
                        )}
                        {!showElitDetails && (
                          <div className={`mt-2 flex items-center gap-1.5 ${t.textMuted}`}>
                            <Shield className="w-3 h-3 text-emerald-500" />
                            <span className="text-[10px]">🛡️ Garanzia 6 mesi: nessun risultato = rimborso totale</span>
                          </div>
                        )}
                      </div>
                      {/* Elit Mockup - Lista attività con priorità */}
                      <div className={`hidden sm:block w-28 rounded-xl p-1.5 flex-shrink-0 ${isDark ? 'bg-slate-800' : 'bg-slate-200'}`}>
                        <div className={`rounded-lg ${isDark ? 'bg-slate-900' : 'bg-white'} p-1.5`}>
                          <div className={`w-6 h-1 mx-auto rounded ${isDark ? 'bg-slate-700' : 'bg-slate-300'} mb-1.5`}></div>
                          <div className={`text-[6px] mb-1.5 px-0.5 ${t.textMuted}`}>🔍 Cerca...</div>
                          {/* Elit business - highlighted */}
                          <div className={`rounded-lg p-1.5 mb-1 border border-emerald-500 ${isDark ? 'bg-emerald-500/10' : 'bg-emerald-50'}`}>
                            <div className="flex items-center gap-1.5">
                              <div className="w-5 h-5 rounded bg-gradient-to-br from-emerald-500 to-cyan-500"></div>
                              <div className="flex-1">
                                <div className="flex items-center gap-0.5">
                                  <span className={`text-[7px] font-bold ${t.text}`}>Top</span>
                                  <span className="text-[5px] bg-emerald-500 text-white px-0.5 rounded font-bold">⭐</span>
                                </div>
                                <span className="text-amber-400 text-[6px]">★★★★★</span>
                              </div>
                            </div>
                          </div>
                          {/* Other businesses - faded */}
                          {[1,2].map(i => (
                            <div key={i} className={`rounded p-1 mb-0.5 opacity-40 ${isDark ? 'bg-slate-800' : 'bg-slate-100'}`}>
                              <div className="flex items-center gap-1">
                                <div className={`w-3 h-3 rounded ${isDark ? 'bg-slate-700' : 'bg-slate-300'}`}></div>
                                <div className={`text-[5px] ${t.textMuted}`}>Altra</div>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>
                </>
              ) : (
                <div ref={paymentSectionRef} className={`backdrop-blur-sm border rounded-xl p-5 md:p-6 ${t.bgCard}`}>
                  <div className="flex items-center gap-3 mb-6">
                    <div className="w-10 h-10 bg-emerald-500/20 rounded-full flex items-center justify-center">
                      <CreditCard className="w-5 h-5 text-emerald-500" />
                    </div>
                    <div>
                      <h3 className={`font-bold ${t.text}`}>Pagamento Sicuro</h3>
                      <p className={`text-sm ${t.textMuted}`}>I tuoi dati sono protetti con crittografia SSL</p>
                    </div>
                  </div>
                  {clientSecret && (
                    <Elements stripe={stripePromise} options={stripeOptions}>
                      <PaymentForm onSuccess={handlePaymentSuccess} onError={handlePaymentError} isProcessing={isProcessing} setIsProcessing={setIsProcessing} isDark={isDark} />
                    </Elements>
                  )}
                  <button onClick={() => { setStep(1); setClientSecret(null); setError(null); }} className={`w-full mt-4 flex items-center justify-center gap-2 py-2 text-sm ${t.textMuted}`}>
                    <ArrowLeft className="w-4 h-4" />Modifica ordine
                  </button>
                </div>
              )}
            </div>

            {/* Right: Summary */}
            <div className="lg:col-span-2 space-y-4">
              {/* Countdown Badge attached to top */}
              <div>
                <CountdownBadge isDark={isDark} />
                <div className={`backdrop-blur-sm border rounded-b-xl rounded-t-none p-4 ${t.bgCard}`}>
                  <h3 className={`text-lg font-bold mb-4 ${t.text}`}>Riepilogo Ordine</h3>
                  <div className="space-y-3 mb-4">
                    <div className="flex justify-between">
                      <span className={t.textSecondary}>{upgradeToPremium ? 'Piano Gestionale' : 'Piano Solo Visibilità'}</span>
                      <span className={t.text}>€{calculateTotal().toFixed(2).replace('.', ',')}{selectedPlanType === 'yearly' ? '/anno' : '/mese'}</span>
                    </div>
                    {addElit && (
                      <div className="flex justify-between">
                        <span className={t.textSecondary}>Programma Elit</span>
                        <span className="text-emerald-500">Gratuito</span>
                      </div>
                  )}
                </div>
                
                {/* Elit Guarantee Badge */}
                {addElit && (
                  <div className={`mb-4 p-3 rounded-xl border-2 border-emerald-500/50 ${isDark ? 'bg-gradient-to-r from-emerald-500/10 to-cyan-500/10' : 'bg-gradient-to-r from-emerald-50 to-cyan-50'}`}>
                    <div className="flex items-center gap-2 mb-2">
                      <Shield className="w-5 h-5 text-emerald-500" />
                      <span className={`font-bold text-sm ${t.text}`}>🛡️ GARANZIA 6 MESI</span>
                    </div>
                    <p className={`text-xs ${t.textSecondary}`}>
                      Se non ricevi <span className="text-emerald-500 font-semibold">almeno 1 cliente</span> in 6 mesi, ti <span className="text-emerald-500 font-semibold">rimborsiamo tutto!</span>
                    </p>
                  </div>
                )}
                
                <div className={`border-t pt-3 mb-4 ${t.border}`}>
                  <div className="flex justify-between text-lg font-bold">
                    <span className={t.text}>Totale</span>
                    <span className="text-emerald-500">€{calculateTotal().toFixed(2).replace('.', ',')}{selectedPlanType === 'yearly' ? '/anno' : '/mese'}</span>
                  </div>
                  {addElit && <p className={`text-xs mt-1 ${t.textMuted}`}>+ 20% su vendite generate da Elit</p>}
                </div>
                
                {step === 1 && (
                  <>
                    <div className="space-y-3 mb-4">
                      <div>
                        <label className={`block text-sm font-medium mb-1.5 ${t.textSecondary}`}>Nome *</label>
                        <input type="text" value={name} onChange={(e) => setName(e.target.value)} placeholder="Il tuo nome" className={`w-full border rounded-lg px-4 py-3 focus:border-emerald-500 focus:outline-none focus:ring-1 focus:ring-emerald-500 ${t.input}`} />
                      </div>
                      <div>
                        <label className={`block text-sm font-medium mb-1.5 ${t.textSecondary}`}>Email *</label>
                        <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="La tua email" className={`w-full border rounded-lg px-4 py-3 focus:border-emerald-500 focus:outline-none focus:ring-1 focus:ring-emerald-500 ${t.input}`} />
                      </div>
                    </div>
                    {error && (
                      <div className="mb-4 p-3 bg-red-500/10 border border-red-500/30 rounded-lg">
                        <p className="text-red-500 text-sm">{error}</p>
                      </div>
                    )}
                    <Button onClick={handleContinueToPayment} disabled={isLoading} className="w-full bg-gradient-to-r from-emerald-500 to-emerald-600 hover:from-emerald-400 hover:to-emerald-500 text-white py-4 rounded-xl font-bold group disabled:opacity-50">
                      {isLoading ? (<><Loader2 className="w-4 h-4 mr-2 animate-spin" />Attendi...</>) : (<>Continua al Pagamento<ArrowRight className="ml-2 w-4 h-4 group-hover:translate-x-1 transition-transform" /></>)}
                    </Button>
                  </>
                )}
                
                {step === 2 && error && (
                  <div className="mb-4 p-3 bg-red-500/10 border border-red-500/30 rounded-lg">
                    <p className="text-red-500 text-sm">{error}</p>
                  </div>
                )}
                
                
                <div className={`mt-4 pt-4 border-t ${t.border} flex items-center justify-center gap-2`}>
                  <Shield className="w-4 h-4 text-emerald-500" />
                  <span className={`text-xs ${t.textMuted}`}>Pagamento sicuro con Stripe</span>
                </div>
                
                <div className="mt-3">
                  <PaymentIcons isDark={isDark} />
                </div>
                </div>
              </div>
              
              {/* Reviews Slider - only show in Step 2 */}
              {step === 2 && <ReviewsSlider isDark={isDark} />}
            </div>
          </div>
          
          {/* Reviews Slider - only show in Step 1 */}
          {step === 1 && <ReviewsSliderFull isDark={isDark} />}
        </div>
      </div>

      {/* Footer */}
      <footer className={`mt-12 py-4 border-t ${isDark ? 'border-slate-800' : 'border-slate-200'}`}>
        <div className="container mx-auto px-4 flex items-center gap-3">
          <button onClick={toggleTheme} className={`flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[10px] md:text-xs border ${t.bgCard}`}>
            {isDark ? <><Sun className="w-3 h-3" /><span>Light</span></> : <><Moon className="w-3 h-3" /><span>Dark</span></>}
          </button>
          <div className={`text-[10px] md:text-xs ${t.textMuted}`}>© 2026 Skipres</div>
        </div>
      </footer>
    </div>
  );
};

export default Checkout;
